import {
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import queryClient from 'lib/queryClient';
import { t } from 'i18next';
import axiosInstance from 'api/axios';
import { type AxiosError } from 'axios';

import {
  type CustomPropertiesListResponse,
  type CustomProperty,
  type CustomPropertyOption,
  type CustomPropertyResponse,
  type EntityProperty,
  type EntityPropertyResponse,
} from 'interfaces/customProperty/customProperty.interface';
import { handleError } from 'utils/handleError/handleError';
import { handleSuccess } from 'utils/handleSuccess/handleSuccess';

export const useCreateTag = (): UseMutationResult<
  CustomProperty,
  Error | AxiosError
> => {
  return useMutation({
    mutationFn: async (params) => {
      const response = await axiosInstance.post('property_definition/', params);
      return response.data;
    },
    onError: (error: Error | AxiosError) => {
      handleError(error);
    },
    onSuccess: async () => {
      handleSuccess(t('assetsPage.TagsTab.toasters.create'));
      await queryClient.invalidateQueries({
        queryKey: ['listCustomProperties'],
      });
    },
  });
};

export const useSetCustomProperty = (): UseMutationResult<
  EntityPropertyResponse,
  Error | AxiosError,
  { params: EntityProperty; assetId: number }
> => {
  return useMutation({
    mutationFn: async ({
      params,
      assetId,
    }: {
      params: EntityProperty;
      assetId: number;
    }) => {
      const response = await axiosInstance.post(
        `entity_property/assets/${assetId}/properties`,
        params
      );
      return response.data;
    },
    onError: (error: Error | AxiosError) => {
      handleError(error);
    },
    onSuccess: async () => {
      handleSuccess(t('assetsPage.TagsTab.toasters.link'));
      await queryClient.invalidateQueries({
        queryKey: ['listCustomProperties'],
      });
    },
  });
};

export const useDeleteCustomProperty = (): UseMutationResult<
  void,
  Error | AxiosError,
  { propertyDefinitionId: number; assetId: number }
> => {
  return useMutation({
    mutationFn: async ({ propertyDefinitionId, assetId }) => {
      await axiosInstance.delete(
        `entity_property/assets/${assetId}/properties/${propertyDefinitionId}`
      );
    },
    onError: (error: Error | AxiosError) => {
      handleError(error);
    },
    onSuccess: async () => {
      handleSuccess(t('assetsPage.TagsTab.toasters.unlink'));
      await queryClient.invalidateQueries({
        queryKey: ['listCustomProperties'],
      });
    },
  });
};

export const useFetchTag = (
  tagId?: number
): UseQueryResult<CustomPropertyResponse> => {
  return useQuery({
    queryKey: ['fetchTag', tagId],
    queryFn: async () => {
      if (!tagId) {
        return null;
      }
      const response = await axiosInstance.get(`property_definition/${tagId}`);
      return response.data;
    },
  });
};

export const useListCustomPropertyOptions = (): UseQueryResult<
  CustomPropertyOption[]
> => {
  return useQuery({
    queryKey: ['listCustomPropertyOptions'],
    queryFn: async () => {
      const response = await axiosInstance.get(`property_definition/options`);
      return response.data;
    },
  });
};

export const useListCustomProperties = ({
  page = 1,
  perPage = 20,
  query = '',
  ...params
}: {
  page?: number;
  perPage?: number;
  query?: string;
  params?: CustomProperty;
} = {}): UseInfiniteQueryResult<InfiniteData<CustomPropertiesListResponse>> => {
  return useInfiniteQuery<CustomPropertiesListResponse>({
    queryKey: ['listCustomProperties', query, params],
    queryFn: async ({ pageParam }) => {
      const response = await axiosInstance.get(`property_definition/list`, {
        params: {
          page: pageParam ?? 1,
          per_page: perPage,
          query,
          ...params,
        },
      });
      return response.data;
    },
    initialPageParam: page,
    getNextPageParam: (_lastGroup, groups) => {
      if (_lastGroup?.hasNextPage) {
        return _lastGroup.nextPage;
      }
    },
  });
};

export const useDeleteTag = (): UseMutationResult<
  void,
  Error | AxiosError<unknown, any>,
  string | number,
  unknown
> => {
  return useMutation({
    mutationFn: async (id) => {
      await axiosInstance.delete(`property_definition/${id}`);
    },
    onError: (error) => {
      handleError(error);
    },
    onSuccess: async () => {
      handleSuccess(t('assetsPage.TagsTab.toasters.delete'));
      await queryClient.invalidateQueries({
        queryKey: ['listCustomProperties'],
      });
    },
  });
};

export const useUpdateTag = (
  propertyDefinitionId?: number
): UseMutationResult<
  void,
  Error | AxiosError,
  Partial<CustomProperty>,
  unknown
> => {
  return useMutation({
    mutationFn: async (params) => {
      if (!propertyDefinitionId) return;
      await axiosInstance.put(
        `property_definition/${propertyDefinitionId}`,
        params
      );
    },
    onError: (error: Error | AxiosError) => {
      handleError(error);
    },
    onSuccess: async () => {
      handleSuccess(t('assetsPage.TagsTab.toasters.update'));
      await queryClient.invalidateQueries({
        queryKey: ['fetchTag', propertyDefinitionId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['listCustomProperties'],
      });
    },
  });
};
